// @flow
import * as R from "ramda"
import queryString from "query-string"
import type { DispatchAPI } from "redux"

import cuckoo from "common/services/cuckoo/cuckoo"
import { get } from "common/utils/get"
import set from "common/utils/set"
import * as starredUtils from "common/records/Starred"
import {
  starredStoredSelector,
  starredItemUrlSelector,
} from "common/services/starred/starredSelectors"
import type PassengersCount from "common/records/PassengersCount"
import type Journey from "common/records/Journey"
import type { GetState } from "common/services/types"
import type { StarredStore, StarredItem, StarredFormData } from "common/records/Starred.types"
import type Place from "common/records/Place"
import type { CabinClass } from "common/records/CabinClass"
import { pageNameSelector } from "common/services/url/navBarLogoUrlSelector"
import { getBasePrice } from "booking/services/prices/reducer"
import PassengersCategoryCount from "common/records/PassengersCount"
import Passenger, {
  PASSENGER_CATEGORIES,
} from "../../../../app/scripts/containers/mutable/Passenger"
import { getThresholds } from "../../../booking/services/states/reducer"

const STARRED_SET_ITEMS = "starred/SET_ITEMS"
const STARRED_ADD_ITEM = "starred/ADD_ITEM"
const STARRED_REMOVE_ITEM = "starred/REMOVE_ITEM"
const STARRED_REMOVE_ALL_ITEMS = "starred/REMOVE_ALL_ITEMS"
const STARRED_UPDATE_VALUES = "starred/UPDATE_VALUES"
const STARRED_SET_SEEN_VALUES = "starred/SET_SEEN_VALUES"
const STARRED_SET_PLACES = "starred/SET_PLACES_SUCCESS"

type StarredSetItemsAction = {
  type: "starred/SET_ITEMS",
  items: Array<StarredItem>,
}

type StarredAddItemAction = {
  type: "starred/ADD_ITEM",
  journey: Journey,
  form: StarredFormData,
}

type StarredRemoveItemAction = {
  type: "starred/REMOVE_ITEM",
  id: string,
}

type StarredRemoveAllItemsAction = {
  type: "starred/REMOVE_ALL_ITEMS",
}

type StarredUpdateValuesAction = {
  type: "starred/UPDATE_VALUES",
  journeys: Array<Journey>,
  passengers: PassengersCount,
  cabinClass: CabinClass,
}

type StarredSetSeenAction = {
  type: "starred/SET_SEEN_VALUES",
  ids: ?Array<string>,
}

type StarredSetPlacesAction = {
  type: "starred/SET_PLACES_SUCCESS",
  res: Array<Place>,
}

type StarredAction =
  | StarredSetItemsAction
  | StarredAddItemAction
  | StarredRemoveItemAction
  | StarredRemoveAllItemsAction
  | StarredUpdateValuesAction
  | StarredSetSeenAction
  | StarredSetPlacesAction

const starredReducerResolver = (state: ?StarredStore, action: StarredAction) => {
  const newState = state || starredStoredSelector(null)

  switch (action.type) {
    case STARRED_SET_ITEMS:
      return set("items", action.items, newState)

    case STARRED_ADD_ITEM:
      return starredUtils.storeAddItem(newState, action.journey, action.form)

    case STARRED_REMOVE_ITEM:
      return starredUtils.storeRemoveItem(newState, action.id)

    case STARRED_REMOVE_ALL_ITEMS:
      return set("items", [], newState)

    case STARRED_UPDATE_VALUES:
      return starredUtils.storeUpdateItems(
        newState,
        action.journeys,
        action.passengers,
        action.cabinClass,
      )

    case STARRED_SET_SEEN_VALUES:
      return starredUtils.storeSetSeenItems(newState, action.ids)

    case STARRED_SET_PLACES:
      return starredUtils.storeSetPlaces(newState, action.res)

    default:
      return newState
  }
}

export default function StarredReducer(state: StarredStore, action: StarredAction) {
  const newState = starredReducerResolver(state, action)

  if (state !== newState) {
    starredUtils.storePersist(newState)
  }

  return newState
}

export const setItems = (items: Array<StarredItem>) => ({
  type: STARRED_SET_ITEMS,
  items,
})

const createBookingStarredFormData = state => {
  const passengers = R.compose(
    R.countBy(p => (p === PASSENGER_CATEGORIES.infant ? "infants" : "adults")),
    R.map(pax =>
      Passenger.determineTypeFromInput(
        pax.birthDay.value,
        pax.birthMonth.value,
        pax.birthYear.value,
        undefined,
        getThresholds(state),
      ),
    ),
    get(["form", "reservation", "passengers"]),
    // $FlowIssue - booking state actually used
  )(state)

  const urlSearch = R.compose(
    queryString.stringify,
    R.omit(["price"]),
    queryString.parse,
    get("location.search"),
  )(window)

  return {
    passengers: new PassengersCategoryCount(passengers),
    returnUrl: `${window.location.pathname}?${urlSearch}`,
    starType: "booking",
  }
}

export const addItem = (journey: Journey) => (dispatch: Dispatch<*>, getState: GetState) => {
  const state = getState()
  const starType = pageNameSelector(state)

  const form = createBookingStarredFormData(state)

  cuckoo.infinario("starFlight", {
    action: "starNew",
  })

  dispatch({
    type: STARRED_ADD_ITEM,
    form,
    journey:
      starType === "search"
        ? journey
        : journey.setIn(["prices", "default"], getBasePrice(state).amount),
  })
}

export const removeItem = (id: string) => ({
  type: STARRED_REMOVE_ITEM,
  id,
})

export const removeAllItems = () => ({
  type: STARRED_REMOVE_ALL_ITEMS,
})

export const updateValues = (
  journeys: Array<Journey>,
  passengers: PassengersCount,
  cabinClass: CabinClass,
) => ({
  type: STARRED_UPDATE_VALUES,
  journeys,
  passengers,
  cabinClass,
})

export const setSeenValues = (ids?: Array<string>) => ({
  type: STARRED_SET_SEEN_VALUES,
  ids,
})

export const goToJourney = (item: StarredItem) => (
  dispatch: DispatchAPI<*>,
  getState: GetState,
) => {
  const state = getState()

  if (item.form.starType === "search") {
    const url = starredItemUrlSelector(state)(item)
    window.location = url
  } else {
    window.location = item.form.returnUrl
  }
}
